import * as React from "react";
import { alpha } from "@mui/material/styles";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import { Link } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../Hooks";
import { GetUserRequestAllOperrator, GetUserRequestOperatorFilter, OperatorTakeOnJob, RemoveRequest, SwitchJob } from "../../../services/api-actions/personal-user-request-action";
import { getSearchUserByFio, getUserInfo, getUserRequestAll } from "../../../store/user-process/selector";
import { Status, UserRole } from "../../../enums/enum";
import { GetAllStatus } from "../../../services/api-actions/dictionaty-action";
import IPersonalUserRequestStatus from "../../../types/dictionary/PersonalUserRequestStatus";
import dayjs from "dayjs";
import { userIsRole } from "../../../utils/user-is-role";
import { SearchUserFio } from "../../../services/api-actions/user-action";
import userDisplayName from "../../../utils/userDisplayName";
import { brand } from "../../../theme";

const pageSizeInfo = 5;

function statusColor(id: number) {
  if (id === Status.Success) return "success" as const;
  if (id === Status.Cansel) return "error" as const;
  if (id === Status.InWork) return "warning" as const;
  return "info" as const;
}

function Meta({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 12, color: brand.muted }}>{label}</Typography>
      <Typography noWrap sx={{ fontSize: 14, fontWeight: 600 }} title={typeof value === "string" ? value : undefined}>
        {value || "—"}
      </Typography>
    </Box>
  );
}

export default function OperatorProcessingRequests() {
  const dispatch = useAppDispatch();
  const [status, setStatus] = React.useState<IPersonalUserRequestStatus[]>([]);
  const [selectStatusId, setSelectStatusId] = React.useState<number>(0);
  const [selectLic, setSelectLic] = React.useState<string>("");
  const [selectOperator] = React.useState<string>("");
  const [switchJob, setSwitchJob] = React.useState<boolean>(false);
  const [selectPage, setSelectPage] = React.useState<number>(0);
  const userRequest = useAppSelector(getUserRequestAll);
  const userInfo = useAppSelector(getUserInfo);

  const searchUserRaw = useAppSelector(getSearchUserByFio);
  // сервер может вернуть не массив (например, страницу ошибки) — тогда показываем пустой список
  const searchUser = (Array.isArray(searchUserRaw) ? searchUserRaw : []).map((item) => {
    return { label: item.email, fio: item.fio?.trim() ?? "", id: item.userId };
  });
  const [searchValue, setSearchValue] = React.useState("");
  const [selectUserInfo, setSelectUserInfo] = React.useState({
    userId: "",
    lic: "",
    fio: "",
  });
  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      dispatch(SearchUserFio({ fio: searchValue }));
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchValue]);

  React.useEffect(() => {
    dispatch(GetAllStatus()).then((result: any) => {
      // если справочник не загрузился, оставляем только «Мои заявки в работе» — иначе вкладка падала
      const loaded = Array.isArray(result?.payload) ? (result.payload as IPersonalUserRequestStatus[]) : [];
      setStatus([...loaded, { id: 0, status: "Мои заявки в работе" } as IPersonalUserRequestStatus]);
    });
  }, [dispatch]);

  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      switch (selectStatusId) {
        case 0:
          dispatch(GetUserRequestAllOperrator());
          break;
        default:
          dispatch(
            GetUserRequestOperatorFilter({ take: pageSizeInfo.toString(), skip: (selectPage * pageSizeInfo).toString(), statusId: selectStatusId.toString(), lic: selectLic })
          );
          break;
      }
    }, 1500);
    return () => clearTimeout(timeoutId);
  }, [selectStatusId, selectPage, selectLic, selectOperator]);

  const reload = (skipPage = selectPage) =>
    dispatch(GetUserRequestOperatorFilter({ take: pageSizeInfo.toString(), skip: (skipPage * pageSizeInfo).toString(), statusId: selectStatusId.toString(), lic: selectLic }));

  const total = userRequest?.[0]?.count ?? 0;
  const pages = Math.ceil(total / pageSizeInfo);
  const currentTitle = status?.filter((x) => x.id == selectStatusId)[0]?.status ?? "";

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={8} lg={9}>
        <Stack direction="row" alignItems="baseline" gap={1.5} sx={{ mb: 2 }}>
          <Typography variant="h6">{currentTitle}</Typography>
          {total > 0 && <Typography sx={{ color: brand.muted, fontSize: 14 }}>всего: {total}</Typography>}
        </Stack>

        {(userRequest == undefined || userRequest?.length === 0) && (
          <Card sx={{ textAlign: "center", py: 5 }}>
            <ForumOutlinedIcon sx={{ fontSize: 40, color: brand.muted }} />
            <Typography sx={{ mt: 1, fontWeight: 600 }}>Заявок нет</Typography>
            <Typography sx={{ color: brand.muted, fontSize: 14 }}>Измените фильтр справа, чтобы увидеть другие заявки.</Typography>
          </Card>
        )}

        <Stack gap={2}>
          {userRequest?.map((item) => (
            <Card key={item.requestId}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={2} sx={{ mb: 1 }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" sx={{ mb: 0.5 }}>
                      <Chip size="small" color={statusColor(item.status.id)} label={item.status.status} />
                      <Typography sx={{ fontSize: 13, color: brand.muted }}>{dayjs(item.createdDate).format("DD.MM.YYYY")}</Typography>
                    </Stack>
                    <Typography variant="h6" sx={{ lineHeight: 1.3 }}>
                      {item.subject.subject}
                    </Typography>
                  </Box>
                  <Button
                    component={Link}
                    to={`../UserRequestChat/${item.requestId}`}
                    target="_blank"
                    variant="outlined"
                    size="small"
                    endIcon={<OpenInNewIcon />}
                    sx={{ flexShrink: 0 }}
                  >
                    Открыть
                  </Button>
                </Stack>

                <Typography
                  sx={{
                    color: brand.ink,
                    fontSize: 14.5,
                    bgcolor: brand.background,
                    borderRadius: 2,
                    p: 1.5,
                    mb: 2,
                    display: "-webkit-box",
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {item.message}
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Meta label="Лицевой счёт" value={!item.lic || item.lic === "undefined" ? "" : item.lic} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Meta label="Почта клиента" value={item.user?.email} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Meta label="Оператор" value={item.status.id == Status.InWork ? userDisplayName(item.operatorUser) : ""} />
                  </Grid>
                </Grid>

                {(item.status.id == Status.Create || switchJob || userIsRole(userInfo, UserRole.OperatorAdmin) || userIsRole(userInfo, UserRole.Admin)) && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Stack direction="row" gap={1} flexWrap="wrap">
                      {item.status.id == Status.Create && (
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<AssignmentTurnedInOutlinedIcon />}
                          onClick={() => {
                            dispatch(OperatorTakeOnJob({ requestId: item.requestId })).then(() => {
                              reload();
                            });
                          }}
                        >
                          Взять в работу
                        </Button>
                      )}
                      {switchJob && (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<PersonAddAltOutlinedIcon />}
                          disabled={!selectUserInfo.userId}
                          onClick={() => {
                            dispatch(SwitchJob({ requestId: item.requestId, userId: selectUserInfo.userId })).then(() => {
                              reload(0);
                            });
                          }}
                        >
                          Назначить оператора
                        </Button>
                      )}
                      {(userIsRole(userInfo, UserRole.OperatorAdmin) || userIsRole(userInfo, UserRole.Admin)) && (
                        <Button
                          color="error"
                          size="small"
                          startIcon={<DeleteOutlineIcon />}
                          sx={{ ml: "auto" }}
                          onClick={() => {
                            dispatch(RemoveRequest({ requestId: item.requestId })).then(() => {
                              reload();
                            });
                          }}
                        >
                          Удалить
                        </Button>
                      )}
                    </Stack>
                  </>
                )}
              </CardContent>
            </Card>
          ))}
        </Stack>

        {total > pageSizeInfo && (
          <Stack alignItems="center" sx={{ mt: 3 }}>
            <Pagination
              count={pages}
              color="primary"
              page={selectPage + 1}
              onChange={(event, pageNumber) => {
                setSelectPage(pageNumber - 1);
              }}
            />
          </Stack>
        )}
      </Grid>

      <Grid item xs={12} md={4} lg={3}>
        <Card sx={{ position: { md: "sticky" }, top: { md: 88 } }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Фильтр заявок
            </Typography>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="request-status-label">Статус</InputLabel>
              <Select
                labelId="request-status-label"
                id="request-status"
                value={selectStatusId}
                label="Статус"
                onChange={(item: any) => {
                  setSelectPage(0);
                  setSelectStatusId(item.target.value);
                }}
              >
                {status?.map((item) => {
                  if (item.status == "В работе" && (userIsRole(userInfo, UserRole.OperatorAdmin) || userIsRole(userInfo, UserRole.Admin)))
                    return (
                      <MenuItem key={item.id} value={item.id}>
                        {item.status}
                      </MenuItem>
                    );
                  if (
                    (item.status == "Новые" || item.status == "Мои заявки в работе" || item.status == "Выполнено" || item.status == "Отменено") &&
                    (userIsRole(userInfo, UserRole.OperatorAdmin) || userIsRole(userInfo, UserRole.Operator) || userIsRole(userInfo, UserRole.Admin))
                  )
                    return (
                      <MenuItem key={item.id} value={item.id}>
                        {item.status}
                      </MenuItem>
                    );
                  return null;
                })}
              </Select>
            </FormControl>

            <TextField
              label="Лицевой счёт"
              placeholder="Например, 720128132"
              fullWidth
              InputLabelProps={{ shrink: true }}
              onChange={(e: any) => {
                setSelectPage(0);
                setSelectLic(e.target.value);
              }}
            />

            <Divider sx={{ my: 2 }} />

            <FormControlLabel
              control={
                <Checkbox
                  checked={switchJob}
                  onChange={(e: any) => {
                    setSwitchJob(e.target.checked);
                  }}
                />
              }
              label="Назначать заявки на оператора"
            />
            {switchJob && (
              <Autocomplete
                sx={{ mt: 1.5 }}
                fullWidth
                // без портала список рисуется внутри карточки и обрезается её краем
                noOptionsText="Ничего не найдено"
                ListboxProps={{ style: { maxHeight: 280 } }}
                options={searchUser ?? []}
                filterOptions={(options) => options}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderOption={(props, option) => (
                  <Box component="li" {...props} key={option.id} sx={{ flexDirection: "column", alignItems: "flex-start !important" }}>
                    <Typography variant="body2">{option.label}</Typography>
                    {option.fio && (
                      <Typography variant="caption" color="text.secondary">
                        {option.fio}
                      </Typography>
                    )}
                  </Box>
                )}
                onChange={(event, newValue) => {
                  setSelectUserInfo({ ...selectUserInfo, userId: newValue?.id ?? "", fio: newValue?.label ?? "" });
                }}
                onInputChange={(event: object, value: any, reason: string) => {
                  if (reason === "reset") return;
                  setSearchValue(value);
                }}
                renderInput={(params) => <TextField {...params} label="Email оператора" />}
              />
            )}
            {switchJob && (
              <Typography sx={{ mt: 1.5, fontSize: 13, color: brand.muted }}>
                Выберите оператора, затем нажмите «Назначить оператора» у нужной заявки.
              </Typography>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}
